import React, { useState, useMemo, useEffect } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher.js';
import { isLocalMode } from '../../utils/apiTarget';
import AILoadingOverlay from '../common/AILoadingOverlay';
import './DataExport.css';
import {
  Download,
  FileSpreadsheet,
  Database,
  FileText,
  CheckCircle,
  AlertCircle,
  Eye,
  Table,
  HardDrive,
  Loader2,
  Info,
  RefreshCw,
  X,
  Check
} from 'lucide-react';
 
const DataExport = ({
  projectId,
  cleanedData,
  projectName = 'DataQuad Export',
  onExportComplete
}) => {
  const { isDarkMode } = useTheme();
 
  // Responsive
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const isMobile = windowWidth < 768;
 
  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
 
  // Export state
  const [activeTab, setActiveTab] = useState('file');
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportResult, setExportResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
 
  // File export state
  const [fileFormat, setFileFormat] = useState('csv');
  const [fileOptions, setFileOptions] = useState({
    encoding: 'utf-8',
    includeHeaders: true,
    includeIndex: false,
    compression: 'none',
    delimiter: ',',
    stataVersion: 118,
    sheetName: 'Data',
    includeLabels: true
  });
 
  // Database export state
  const [databaseType, setDatabaseType] = useState('postgresql');
  const [dbConfig, setDbConfig] = useState({
    host: '',
    port: '5432',
    database: '',
    username: '',
    password: '',
    tableName: '',
    ifExists: 'replace',
    region: 'us-east-1',
    accessKeyId: '',
    secretAccessKey: ''
  });
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
 
  // ------------------------------------------------------------------
  // Data helpers
  // ------------------------------------------------------------------
  const extractDataArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data.full_data && Array.isArray(data.full_data)) return data.full_data;
    if (data.data && Array.isArray(data.data)) return data.data;
    return [];
  };
 
  const currentData = useMemo(() => extractDataArray(cleanedData), [cleanedData]);
 
  const dataStats = useMemo(() => {
    if (!currentData || currentData.length === 0) {
      return { rows: 0, columns: 0, size: '0 KB' };
    }
    const rows = currentData.length;
    const columns = Object.keys(currentData[0] || {}).length;
    const jsonSize = JSON.stringify(currentData).length;
    const sizeKB = (jsonSize / 1024).toFixed(1);
    const sizeMB = (jsonSize / (1024 * 1024)).toFixed(2);
    const size = jsonSize > 1024 * 1024 ? `${sizeMB} MB` : `${sizeKB} KB`;
    return { rows, columns, size };
  }, [currentData]);
 
  const fileFormats = [
    { id: 'csv',   name: 'CSV',   ext: '.csv'   },
    { id: 'json',  name: 'JSON',  ext: '.json'  },
    { id: 'xlsx',  name: 'Excel', ext: '.xlsx'  },
    { id: 'stata', name: 'Stata', ext: '.dta'   },
    { id: 'spss',  name: 'SPSS',  ext: '.sav'   },
  ];
 
  // MongoDB & DynamoDB export run cloud-side only; the desktop engine does
  // not bundle their drivers. Hidden for local projects.
  const allDatabaseOptions = [
    { id: 'postgresql', name: 'PostgreSQL', defaultPort: '5432'  },
    { id: 'mysql',      name: 'MySQL',      defaultPort: '3306'  },
    { id: 'mariadb',    name: 'MariaDB',    defaultPort: '3306'  },
    { id: 'mongodb',    name: 'MongoDB',    defaultPort: '27017' },
    { id: 'dynamodb',   name: 'DynamoDB',   defaultPort: '443'   },
  ];
  const databaseOptions = isLocalMode()
    ? allDatabaseOptions.filter(o => o.id !== 'mongodb' && o.id !== 'dynamodb')
    : allDatabaseOptions;
 
  // ------------------------------------------------------------------
  // Handlers — logic preserved from original
  // ------------------------------------------------------------------
  const handleFileExport = async () => {
    if (!currentData || currentData.length === 0) {
      setExportResult({ success: false, message: 'No data available to export' });
      return;
    }
 
    setIsExporting(true);
    setExportProgress(0);
    setExportResult(null);
 
    try {
      const progressInterval = setInterval(() => {
        setExportProgress(prev => Math.min(prev + 10, 90));
      }, 200);
 
      let blob;
      let filename = `${projectName.replace(/[^a-z0-9]/gi, '_')}_cleaned_${new Date().toISOString().split('T')[0]}`;
 
      const backendFormats = ['xlsx', 'stata', 'spss', 'sas', 'parquet', 'rds'];
 
      if (backendFormats.includes(fileFormat)) {
        try {
          const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
          const response = await fetch(`${backendUrl}/api/export/file/direct`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              data: currentData,
              format: fileFormat,
              filename: `${projectName.replace(/[^a-z0-9]/gi, '_')}_cleaned`,
              encoding: fileOptions.encoding,
              delimiter: fileOptions.delimiter,
              include_headers: fileOptions.includeHeaders,
              include_index: fileOptions.includeIndex,
              include_variable_labels: fileOptions.includeLabels,
              compression: fileOptions.compression || 'none',
            })
          });
 
          if (response.ok) {
            blob = await response.blob();
            const contentDisposition = response.headers.get('Content-Disposition');
            if (contentDisposition) {
              const match = contentDisposition.match(/filename=(.+)/);
              if (match) filename = match[1].replace(/"/g, '');
            } else {
              const extensions = { xlsx: '.xlsx', stata: '.dta', spss: '.sav', sas: '.sas7bdat', parquet: '.parquet', rds: '.rds' };
              filename += extensions[fileFormat] || '.dat';
            }
          } else {
            const errorData = await response.json().catch(() => ({}));
            console.warn('Backend export failed:', errorData.detail || response.statusText);
            throw new Error(errorData.detail || 'Backend export failed');
          }
        } catch (backendError) {
          console.warn('Using CSV fallback for', fileFormat, ':', backendError.message);
          const headers = Object.keys(currentData[0]);
          const csvContent = [
            headers.join(','),
            ...currentData.map(row => headers.map(h => {
              const val = row[h];
              if (val === null || val === undefined) return fileFormat === 'stata' ? '.' : '';
              const strVal = String(val);
              if (strVal.includes(',') || strVal.includes('"') || strVal.includes('\n')) {
                return `"${strVal.replace(/"/g, '""')}"`;
              }
              return strVal;
            }).join(','))
          ].join('\n');
 
          blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
          filename += `_for_${fileFormat}_import.csv`;
 
          clearInterval(progressInterval);
          setExportProgress(100);
          setIsExporting(false);
          setExportResult({
            success: true,
            message: `Exported as CSV (importable into ${fileFormats.find(f => f.id === fileFormat)?.name}). Backend error: ${backendError.message}`,
            filename
          });
 
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
 
          if (onExportComplete) {
            onExportComplete({ format: fileFormat, filename, rows: dataStats.rows, fallback: true });
          }
          return;
        }
      } else if (fileFormat === 'csv') {
        const headers = Object.keys(currentData[0]);
        const csvContent = [
          fileOptions.includeHeaders ? headers.join(fileOptions.delimiter) : null,
          ...currentData.map((row, idx) => {
            const values = headers.map(h => {
              const val = row[h];
              if (val === null || val === undefined) return '';
              const strVal = String(val);
              if (strVal.includes(fileOptions.delimiter) || strVal.includes('"') || strVal.includes('\n')) {
                return `"${strVal.replace(/"/g, '""')}"`;
              }
              return strVal;
            });
            return (fileOptions.includeIndex ? `${idx},` : '') + values.join(fileOptions.delimiter);
          })
        ].filter(Boolean).join('\n');
 
        blob = new Blob([csvContent], { type: 'text/csv;charset=' + fileOptions.encoding });
        filename += '.csv';
      } else if (fileFormat === 'json') {
        const jsonContent = JSON.stringify(currentData, null, 2);
        blob = new Blob([jsonContent], { type: 'application/json' });
        filename += '.json';
      }
 
      clearInterval(progressInterval);
      setExportProgress(100);
 
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
 
      setExportResult({
        success: true,
        message: `Successfully exported ${dataStats.rows.toLocaleString()} rows to ${filename}`
      });
 
      if (onExportComplete) {
        onExportComplete({ format: fileFormat, filename, rows: dataStats.rows });
      }
    } catch (error) {
      console.error('Export error:', error);
      setExportResult({ success: false, message: `Export failed: ${error.message}` });
    } finally {
      setIsExporting(false);
    }
  };
 
  const handleDatabaseExport = async () => {
    if (!currentData || currentData.length === 0) {
      setExportResult({ success: false, message: 'No data available to export' });
      return;
    }
 
    if (databaseType === 'dynamodb') {
      if (!dbConfig.accessKeyId || !dbConfig.secretAccessKey || !dbConfig.tableName) {
        setExportResult({ success: false, message: 'Please fill in AWS credentials and table name' });
        return;
      }
    } else {
      if (!dbConfig.host || !dbConfig.database || !dbConfig.tableName) {
        setExportResult({ success: false, message: 'Please fill in all required database fields' });
        return;
      }
    }
 
    setIsExporting(true);
    setExportProgress(0);
    setExportResult(null);
 
    try {
      const progressInterval = setInterval(() => {
        setExportProgress(prev => Math.min(prev + 5, 90));
      }, 300);
 
      let exportRequest = {
        data: currentData,
        database_type: databaseType,
        table_name: dbConfig.tableName,
        create_table: true,
        replace_existing: dbConfig.ifExists === 'replace',
        batch_size: 1000
      };
 
      if (databaseType === 'dynamodb') {
        exportRequest = {
          ...exportRequest,
          region: dbConfig.region || 'us-east-1',
          access_key_id: dbConfig.accessKeyId,
          secret_access_key: dbConfig.secretAccessKey
        };
      } else {
        exportRequest = {
          ...exportRequest,
          host: dbConfig.host,
          port: parseInt(dbConfig.port) || 5432,
          database: dbConfig.database,
          username: dbConfig.username,
          password: dbConfig.password,
          schema_name: dbConfig.schema || null
        };
      }
 
      const response = await fetch(`${API_BASE_URL}/api/export/database/direct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(exportRequest)
      });
 
      clearInterval(progressInterval);
 
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Export failed with status ${response.status}`);
      }
 
      const result = await response.json();
      setExportProgress(100);
 
      if (result.success) {
        const targetLocation = databaseType === 'dynamodb'
          ? `dynamodb://${dbConfig.region}/${dbConfig.tableName}`
          : `${databaseType}://${dbConfig.host}/${dbConfig.database}.${dbConfig.tableName}`;
        setExportResult({
          success: true,
          message: `Successfully exported ${result.rows_exported?.toLocaleString() || dataStats.rows.toLocaleString()} rows to ${targetLocation}`
        });
      } else {
        throw new Error(result.message || 'Export failed');
      }
    } catch (error) {
      console.error('Database export error:', error);
      setExportResult({ success: false, message: `Export failed: ${error.message}` });
    } finally {
      setIsExporting(false);
    }
  };
 
  const testConnection = async () => {
    setIsTestingConnection(true);
    setConnectionStatus(null);
 
    try {
      if (databaseType === 'dynamodb') {
        if (!dbConfig.accessKeyId || !dbConfig.secretAccessKey) {
          setConnectionStatus({ success: false, message: 'Please fill in AWS credentials' });
          return;
        }
      } else {
        if (!dbConfig.host || !dbConfig.database) {
          setConnectionStatus({ success: false, message: 'Please fill in host and database' });
          return;
        }
      }
 
      let testRequest = {
        project_id: 'test',
        data_source: 'cleaned',
        database_type: databaseType,
        table_name: dbConfig.tableName || 'test_table',
        create_table: false,
        replace_existing: false
      };
 
      if (databaseType === 'dynamodb') {
        testRequest = {
          ...testRequest,
          region: dbConfig.region || 'us-east-1',
          access_key_id: dbConfig.accessKeyId,
          secret_access_key: dbConfig.secretAccessKey
        };
      } else {
        testRequest = {
          ...testRequest,
          host: dbConfig.host,
          port: parseInt(dbConfig.port) || 5432,
          database: dbConfig.database,
          username: dbConfig.username,
          password: dbConfig.password
        };
      }
 
      const response = await fetch(`${API_BASE_URL}/api/export/database/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testRequest)
      });
 
      const result = await response.json();
 
      if (result.success) {
        setConnectionStatus({
          success: true,
          message: `Connection successful!${result.server_version ? ` (${result.server_version.substring(0, 50)})` : ''}`
        });
      } else {
        setConnectionStatus({ success: false, message: result.message || 'Connection failed' });
      }
    } catch (error) {
      console.error('Connection test error:', error);
      setConnectionStatus({ success: false, message: `Connection failed: ${error.message}` });
    } finally {
      setIsTestingConnection(false);
    }
  };
 
  // ------------------------------------------------------------------
  // Preview
  // ------------------------------------------------------------------
  const renderDataPreview = () => {
    if (!currentData || currentData.length === 0) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', color: isDarkMode ? '#6b6862' : '#908c82', fontSize: '13px' }}>
          No data available for preview
        </div>
      );
    }
 
    const headers = Object.keys(currentData[0]);
    const previewRows = currentData.slice(0, 5);
    const borderColor = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const headerBg = isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)';
    const text = isDarkMode ? '#f0ede4' : '#1a1a1a';
    const textSub = isDarkMode ? '#a8a49a' : '#555550';
    const textMuted = isDarkMode ? '#6b6862' : '#908c82';
 
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr>
              {headers.slice(0, 8).map(header => (
                <th key={header} style={{
                  padding: '10px 12px',
                  textAlign: 'left',
                  borderBottom: `0.5px solid ${borderColor}`,
                  backgroundColor: headerBg,
                  color: textSub,
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap'
                }}>
                  {header}
                </th>
              ))}
              {headers.length > 8 && (
                <th style={{
                  padding: '10px 12px', textAlign: 'center',
                  borderBottom: `0.5px solid ${borderColor}`,
                  backgroundColor: headerBg, color: textMuted,
                  fontStyle: 'italic', fontSize: '11px'
                }}>
                  +{headers.length - 8} more
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row, idx) => (
              <tr key={idx}>
                {headers.slice(0, 8).map(header => (
                  <td key={header} style={{
                    padding: '9px 12px',
                    borderBottom: `0.5px solid ${borderColor}`,
                    color: text,
                    maxWidth: '200px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {row[header] ?? <span style={{ color: textMuted }}>—</span>}
                  </td>
                ))}
                {headers.length > 8 && (
                  <td style={{
                    padding: '9px 12px',
                    borderBottom: `0.5px solid ${borderColor}`,
                    color: textMuted,
                    textAlign: 'center',
                  }}>
                    …
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {currentData.length > 5 && (
          <div style={{
            padding: '10px', textAlign: 'center',
            color: textMuted,
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '0.06em',
            textTransform: 'lowercase',
            borderTop: `0.5px solid ${borderColor}`,
          }}>
            showing 5 of {(currentData?.length || 0).toLocaleString()} rows
          </div>
        )}
      </div>
    );
  };
 
  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  const isDbValid = databaseType === 'dynamodb'
    ? (dbConfig.accessKeyId && dbConfig.secretAccessKey)
    : (dbConfig.host && dbConfig.database);
 
  return (
    <div
      className={`data-export-page ${isDarkMode ? 'dark' : 'light'}`}
      style={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: 'transparent',
        padding: '24px 32px 40px',
        fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* Fullscreen Loading */}
      <AILoadingOverlay
        isVisible={isExporting}
        context="data_loading"
        title={activeTab === 'file'
          ? `Exporting to ${fileFormats.find(f => f.id === fileFormat)?.name || 'file'}...`
          : `Exporting to ${databaseOptions.find(d => d.id === databaseType)?.name || 'database'}...`
        }
        subtitle={activeTab === 'file'
          ? `Preparing ${dataStats.rows.toLocaleString()} rows for download`
          : `Connecting and transferring ${dataStats.rows.toLocaleString()} rows to database`
        }
        realTimeStats={{ totalRows: dataStats.rows, columnsAnalyzed: dataStats.columns }}
        showDetailedLogs={true}
      />
 
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
 
        {/* Header */}
        <div className="dqe-header">
          <div className="dqe-header-left">
            <div className="dqe-header-icon">
              <Download size={24} />
            </div>
            <h1 className="dqe-title">Data Export</h1>
          </div>
        </div>
 
        {/* Source Card */}
        <div className="dqe-source-card">
          <div className="dqe-source-badge">
            <CheckCircle size={18} />
          </div>
          <div className="dqe-source-meta">
            <p className="dqe-source-title">Cleaned Data</p>
            <p className="dqe-source-sub">ready to export</p>
          </div>
          <div className="dqe-source-stats">
            <div className="stat">
              <span className="stat-label">rows</span>
              <span className="stat-value">{dataStats.rows.toLocaleString()}</span>
            </div>
            <div className="divider" />
            <div className="stat">
              <span className="stat-label">columns</span>
              <span className="stat-value">{dataStats.columns.toLocaleString()}</span>
            </div>
            <div className="divider" />
            <div className="stat">
              <span className="stat-label">est. size</span>
              <span className="stat-value">{dataStats.size}</span>
            </div>
          </div>
          <button className="dqe-preview-btn" onClick={() => setShowPreview(!showPreview)}>
            <Eye size={13} />
            {showPreview ? 'hide preview' : 'show preview'}
          </button>
        </div>
 
        {/* Preview */}
        {showPreview && (
          <div className="dqe-preview-wrap">
            {renderDataPreview()}
          </div>
        )}
 
        {/* Top-level tabs: File / Database */}
        <div className="dqe-tabs-container">
          <div className="dqe-tabs">
            {[
              { id: 'file',     label: 'File Export',     icon: HardDrive },
              { id: 'database', label: 'Database Export', icon: Database  },
            ].map(tab => {
              const I = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  className={`dqe-tab ${active ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <I size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
 
        {/* File Export Panel */}
        {activeTab === 'file' && (
          <div className="dqe-panel with-nodes">
            <span className="cn-bl" /><span className="cn-br" />
 
            {/* Format sub-tabs */}
            <div style={{ padding: isMobile ? '12px 16px 0' : '16px 24px 0' }}>
              <div className="dqe-subtabs">
                {fileFormats.map(format => {
                  const active = fileFormat === format.id;
                  return (
                    <button
                      key={format.id}
                      className={`dqe-subtab ${active ? 'active' : ''}`}
                      onClick={() => setFileFormat(format.id)}
                    >
                      {format.name}
                      <span className="dqe-subtab-ext">{format.ext}</span>
                    </button>
                  );
                })}
              </div>
            </div>
 
            {/* Options */}
            <div className="dqe-panel-section" style={{ borderTop: 'none' }}>
              <div className="dqe-kicker">export options</div>
              <div
                className="dqe-form-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px',
                }}
              >
                <label className="dqe-check">
                  <input
                    type="checkbox"
                    checked={fileOptions.includeHeaders}
                    onChange={(e) => setFileOptions(prev => ({ ...prev, includeHeaders: e.target.checked }))}
                  />
                  Include column headers
                </label>
 
                <label className="dqe-check">
                  <input
                    type="checkbox"
                    checked={fileOptions.includeIndex}
                    onChange={(e) => setFileOptions(prev => ({ ...prev, includeIndex: e.target.checked }))}
                  />
                  Include row index
                </label>
 
                {fileFormat === 'csv' && (
                  <>
                    <div>
                      <label className="dqe-label">Delimiter</label>
                      <select
                        className="dqe-select"
                        value={fileOptions.delimiter}
                        onChange={(e) => setFileOptions(prev => ({ ...prev, delimiter: e.target.value }))}
                      >
                        <option value=",">Comma (,)</option>
                        <option value=";">Semicolon (;)</option>
                        <option value={"\t"}>Tab</option>
                        <option value="|">Pipe (|)</option>
                      </select>
                    </div>
                    <div>
                      <label className="dqe-label">Encoding</label>
                      <select
                        className="dqe-select"
                        value={fileOptions.encoding}
                        onChange={(e) => setFileOptions(prev => ({ ...prev, encoding: e.target.value }))}
                      >
                        <option value="utf-8">UTF-8</option>
                        <option value="utf-16">UTF-16</option>
                        <option value="iso-8859-1">ISO-8859-1 (Latin-1)</option>
                        <option value="windows-1252">Windows-1252</option>
                      </select>
                    </div>
                  </>
                )}
 
                {fileFormat === 'xlsx' && (
                  <div style={{ gridColumn: isMobile ? '1 / -1' : undefined }}>
                    <label className="dqe-label">Sheet Name</label>
                    <input
                      className="dqe-input"
                      type="text"
                      value={fileOptions.sheetName}
                      onChange={(e) => setFileOptions(prev => ({ ...prev, sheetName: e.target.value }))}
                    />
                  </div>
                )}
 
                {(fileFormat === 'stata' || fileFormat === 'spss') && (
                  <div
                    style={{
                      gridColumn: '1 / -1',
                      padding: '12px 14px',
                      backgroundColor: isDarkMode ? 'rgba(180, 83, 9, 0.12)' : 'rgba(217, 119, 6, 0.08)',
                      border: `0.5px solid ${isDarkMode ? 'rgba(180, 83, 9, 0.35)' : 'rgba(217, 119, 6, 0.25)'}`,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                    }}
                  >
                    <Info size={16} color={isDarkMode ? '#fbbf24' : '#b45309'} style={{ flexShrink: 0, marginTop: '1px' }} />
                    <div style={{ fontSize: '12.5px', lineHeight: 1.5, color: isDarkMode ? '#f0ede4' : '#1a1a1a' }}>
                      <strong style={{ fontWeight: 500 }}>Note:</strong> Full {fileFormat === 'stata' ? 'Stata .dta' : 'SPSS .sav'} format with
                      variable labels requires backend processing. Export will fall back to CSV (importable into
                      {fileFormat === 'stata' ? ' Stata' : ' SPSS'}).
                    </div>
                  </div>
                )}
              </div>
            </div>
 
            {/* Action footer */}
            <div className="dqe-action-footer">
              <div className="dqe-action-note">
                Ready to export <strong>{dataStats.rows.toLocaleString()}</strong> rows as {fileFormats.find(f => f.id === fileFormat)?.name}
              </div>
              <button
                className="dqe-cta"
                onClick={handleFileExport}
                disabled={isExporting || !currentData?.length}
              >
                {isExporting ? (
                  <>
                    <Loader2 size={16} className="dqe-spinner" />
                    <span>Exporting… {exportProgress}%</span>
                  </>
                ) : (
                  <>
                    <span>Export file</span>
                    <Download size={14} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
 
        {/* Database Export Panel */}
        {activeTab === 'database' && (
          <div className="dqe-panel with-nodes">
            <span className="cn-bl" /><span className="cn-br" />
 
            {/* Database sub-tabs */}
            <div style={{ padding: isMobile ? '12px 16px 0' : '16px 24px 0' }}>
              <div className="dqe-subtabs">
                {databaseOptions.map(db => {
                  const active = databaseType === db.id;
                  return (
                    <button
                      key={db.id}
                      className={`dqe-subtab ${active ? 'active' : ''}`}
                      onClick={() => {
                        setDatabaseType(db.id);
                        setDbConfig(prev => ({ ...prev, port: db.defaultPort }));
                        setConnectionStatus(null);
                      }}
                    >
                      {db.name}
                    </button>
                  );
                })}
              </div>
            </div>
 
            {/* Connection config */}
            <div className="dqe-panel-section" style={{ borderTop: 'none' }}>
              <div className="dqe-kicker">connection details</div>
 
              {databaseType === 'dynamodb' ? (
                <div className="dqe-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div>
                    <label className="dqe-label">AWS Region *</label>
                    <select
                      className="dqe-select"
                      value={dbConfig.region}
                      onChange={(e) => setDbConfig(prev => ({ ...prev, region: e.target.value }))}
                    >
                      <option value="us-east-1">US East (N. Virginia)</option>
                      <option value="us-east-2">US East (Ohio)</option>
                      <option value="us-west-1">US West (N. California)</option>
                      <option value="us-west-2">US West (Oregon)</option>
                      <option value="eu-west-1">EU (Ireland)</option>
                      <option value="eu-west-2">EU (London)</option>
                      <option value="eu-central-1">EU (Frankfurt)</option>
                      <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                      <option value="ap-southeast-2">Asia Pacific (Sydney)</option>
                      <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
                      <option value="af-south-1">Africa (Cape Town)</option>
                    </select>
                  </div>
                  <div>
                    <label className="dqe-label">Table Name *</label>
                    <input
                      className="dqe-input"
                      type="text"
                      placeholder="DynamoDB table name"
                      value={dbConfig.tableName}
                      onChange={(e) => setDbConfig(prev => ({ ...prev, tableName: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="dqe-label">Access Key ID *</label>
                    <input
                      className="dqe-input"
                      type="text"
                      placeholder="AWS Access Key ID"
                      value={dbConfig.accessKeyId}
                      onChange={(e) => setDbConfig(prev => ({ ...prev, accessKeyId: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="dqe-label">Secret Access Key *</label>
                    <input
                      className="dqe-input"
                      type="password"
                      placeholder="AWS Secret Access Key"
                      value={dbConfig.secretAccessKey}
                      onChange={(e) => setDbConfig(prev => ({ ...prev, secretAccessKey: e.target.value }))}
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="dqe-label">If Table Exists</label>
                    <select
                      className="dqe-select"
                      value={dbConfig.ifExists}
                      onChange={(e) => setDbConfig(prev => ({ ...prev, ifExists: e.target.value }))}
                    >
                      <option value="replace">Replace (delete and recreate)</option>
                      <option value="append">Append (add items)</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="dqe-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div>
                    <label className="dqe-label">Host *</label>
                    <input
                      className="dqe-input"
                      type="text"
                      placeholder="localhost or IP address"
                      value={dbConfig.host}
                      onChange={(e) => setDbConfig(prev => ({ ...prev, host: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="dqe-label">Port</label>
                    <input
                      className="dqe-input"
                      type="text"
                      placeholder={databaseOptions.find(d => d.id === databaseType)?.defaultPort}
                      value={dbConfig.port}
                      onChange={(e) => setDbConfig(prev => ({ ...prev, port: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="dqe-label">Database *</label>
                    <input
                      className="dqe-input"
                      type="text"
                      placeholder="Database name"
                      value={dbConfig.database}
                      onChange={(e) => setDbConfig(prev => ({ ...prev, database: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="dqe-label">
                      {databaseType === 'mongodb' ? 'Collection Name *' : 'Table Name *'}
                    </label>
                    <input
                      className="dqe-input"
                      type="text"
                      placeholder={databaseType === 'mongodb' ? 'Collection to create/update' : 'Table to create/update'}
                      value={dbConfig.tableName}
                      onChange={(e) => setDbConfig(prev => ({ ...prev, tableName: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="dqe-label">Username {databaseType === 'mongodb' ? '(optional)' : ''}</label>
                    <input
                      className="dqe-input"
                      type="text"
                      placeholder="Database username"
                      value={dbConfig.username}
                      onChange={(e) => setDbConfig(prev => ({ ...prev, username: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="dqe-label">Password {databaseType === 'mongodb' ? '(optional)' : ''}</label>
                    <input
                      className="dqe-input"
                      type="password"
                      placeholder="Database password"
                      value={dbConfig.password}
                      onChange={(e) => setDbConfig(prev => ({ ...prev, password: e.target.value }))}
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="dqe-label">
                      If {databaseType === 'mongodb' ? 'Collection' : 'Table'} Exists
                    </label>
                    <select
                      className="dqe-select"
                      value={dbConfig.ifExists}
                      onChange={(e) => setDbConfig(prev => ({ ...prev, ifExists: e.target.value }))}
                    >
                      <option value="replace">Replace (drop and recreate)</option>
                      <option value="append">Append (add rows)</option>
                      <option value="fail">Fail (raise error)</option>
                    </select>
                  </div>
                </div>
              )}
 
              {/* Test connection row */}
              <div
                className="dqe-conn-row"
                style={{ marginTop: '18px', display: 'flex', alignItems: 'center', gap: '14px' }}
              >
                <button
                  className="dqe-cta-secondary"
                  onClick={testConnection}
                  disabled={isTestingConnection || !isDbValid}
                >
                  {isTestingConnection ? (
                    <Loader2 size={14} className="dqe-spinner" />
                  ) : (
                    <RefreshCw size={14} />
                  )}
                  Test connection
                </button>
                {connectionStatus && (
                  <div className={`dqe-conn-status ${connectionStatus.success ? 'success' : 'error'}`}>
                    {connectionStatus.success ? <Check size={14} /> : <X size={14} />}
                    {connectionStatus.message}
                  </div>
                )}
              </div>
            </div>
 
            {/* Action footer */}
            <div className="dqe-action-footer">
              <div className="dqe-action-note">
                Ready to export <strong>{dataStats.rows.toLocaleString()}</strong> rows to {databaseOptions.find(d => d.id === databaseType)?.name}
              </div>
              <button
                className="dqe-cta"
                onClick={handleDatabaseExport}
                disabled={isExporting || !currentData?.length || !dbConfig.tableName || !isDbValid}
              >
                {isExporting ? (
                  <>
                    <Loader2 size={16} className="dqe-spinner" />
                    <span>Exporting… {exportProgress}%</span>
                  </>
                ) : (
                  <>
                    <span>Export to database</span>
                    <Database size={14} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
 
        {/* Result banner */}
        {exportResult && (
          <div className={`dqe-result ${exportResult.success ? 'success' : 'error'}`}>
            {exportResult.success ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
            <span className="dqe-result-msg">{exportResult.message}</span>
            <button className="dqe-result-close" onClick={() => setExportResult(null)}>
              <X size={16} />
            </button>
          </div>
        )}
 
        {/* Tips */}
        <div className="dqe-tips">
          <div className="dqe-kicker">export tips</div>
          <div className="dqe-tips-grid">
            {[
              'CSV is the most universal format — compatible with every analysis tool.',
              'Stata (.dta) and SPSS (.sav) preserve variable labels, essential for survey data.',
              'For very large datasets, a database export is faster than file download.',
              'Always run Test Connection before committing a full database export.',
            ].map((tip, i) => (
              <div key={i} className="dqe-tip">
                <span className="dqe-tip-num">{i + 1}</span>
                <span>{tip}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
 
export default DataExport;